import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { User, Permission } from "../types";
import { getPermissionKey } from "../utils/permissions";

interface AuthContextType {
  user: User | null;
  permissions: Permission[];
  resourceLabelMap: Record<string, string>;
  refreshCurrentUser: () => Promise<void>;
  login: (token: string, user: User) => void;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  isSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [permissions, setPermissions] = useState<Permission[]>(() => {
    const savedPermissions = localStorage.getItem("permissions");
    return savedPermissions ? JSON.parse(savedPermissions) : [];
  });

  // Computed values
  const isAuthenticated = !!localStorage.getItem("token");

  const resourceLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    permissions.forEach((perm) => {
      if (perm.resource && perm.resourceLabel && !map[perm.resource]) {
        map[perm.resource] = perm.resourceLabel;
      }
    });
    return map;
  }, [permissions]);

  const refreshCurrentUser = useCallback(async () => {
    const token = localStorage.getItem("token");

    if (!token) {
      return;
    }

    try {
      const { auth } = await import("../services/api");
      const response = await auth.getMe();
      const result = response.result;
      const rolePermissions = Array.isArray(result?.role?.permissions)
        ? result.role.permissions
        : [];
      const populatedPermissions = Array.isArray(result?.role?.permissions)
        ? rolePermissions.filter(
            (permission: Permission | string): permission is Permission =>
              typeof permission === "object" && permission !== null,
          )
        : [];
      const normalizedUser: User = {
        ...result,
      };

      setUser(normalizedUser);
      setPermissions(populatedPermissions);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      localStorage.setItem("permissions", JSON.stringify(populatedPermissions));
    } catch (error) {
      console.error("Failed to refresh current user:", error);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      localStorage.removeItem("user");
      localStorage.removeItem("permissions");
      setPermissions([]);
      setUser(null);
    }
  }, []);

  const login = (newToken: string, newUser: User) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("user", JSON.stringify(newUser));
    localStorage.removeItem("permissions");
    setPermissions([]);
    setUser(newUser);
  };

  const logout = async () => {
    try {
      // Call logout API to revoke token on server
      const { auth } = await import("../services/api");
      await auth.logout();
    } catch (error) {
      console.error("Logout API error:", error);
      // Continue with local logout even if API call fails
    } finally {
      // Clear local state
      setUser(null);
      setPermissions([]);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("permissions");
    }
  };

  // Permission helper functions
  const isSuperAdmin = (): boolean => {
    return user?.role?.isSuperAdmin === true;
  };

  const hasPermission = (permission: string): boolean => {
    // Super admin has all permissions
    if (isSuperAdmin()) {
      return true;
    }

    // Legacy superAdmin support: if role value is "superAdmin" and permissions field doesn't exist, grant access
    if (user?.role?.value === "superAdmin" && !user?.role?.permissions) {
      return true;
    }

    // Check if user has the permission in their role
    return (
      user?.role?.permissions?.some(
        (item) => getPermissionKey(item) === permission,
      ) || false
    );
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    // Super admin has all permissions
    if (isSuperAdmin()) {
      return true;
    }

    // User needs at least one of the permissions (OR logic)
    if (!permissions || permissions.length === 0) {
      return true;
    }

    return permissions.some((perm) => hasPermission(perm));
  };

  const hasAllPermissions = (permissions: string[]): boolean => {
    // Super admin has all permissions
    if (isSuperAdmin()) {
      return true;
    }

    // User needs all of the permissions (AND logic)
    if (!permissions || permissions.length === 0) {
      return true;
    }

    return permissions.every((perm) => hasPermission(perm));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        resourceLabelMap,
        refreshCurrentUser,
        login,
        logout,
        isAuthenticated,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        isSuperAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
